import clsx from 'clsx'
import { LogOut, Maximize2, Minimize2, Settings } from 'lucide-react'
import transvoltLogo from '../assets/transvolt-logo-black.svg'
import { logoutHref } from '../auth/loginHint'
import { useSession } from '../auth/useSession'
import { visibleFleetPills } from './fleetNav'

export interface TopBarNav {
  id: string
  label: string
}

export interface TopBarProps {
  clock: string
  live: boolean
  rate?: string
  gateways?: number
  user?: string
  /** Fleet (Ultratech / MBMT). Shown first; surfaces sit beside it. */
  fleets?: TopBarNav[]
  activeFleet?: string
  onFleet?: (id: string) => void
  /**
   * Surface inside the current fleet (Command/Reports or Live/Reports).
   */
  nav?: TopBarNav[]
  activeNav?: string
  onNav?: (id: string) => void
  /** Gear icon — opens Settings / master data. */
  onSettings?: () => void
  settingsActive?: boolean
  /** Viewport map with live vehicles only — no panels, drawers, or overlay chips. */
  fullMap?: boolean
  onFullMap?: () => void
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.charAt(0) ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) ?? '' : ''
  return (first + last).toUpperCase() || '—'
}

function PillNav({
  items, active, onPick, label,
}: {
  items: TopBarNav[]
  active?: string
  onPick?: (id: string) => void
  label: string
}): JSX.Element {
  return (
    <nav aria-label={label} className="flex items-center gap-0.5 rounded-full bg-[#EEF2F7] p-0.5">
      {items.map((n) => (
        <button
          key={n.id}
          type="button"
          onClick={() => onPick?.(n.id)}
          aria-current={active === n.id ? 'page' : undefined}
          className={clsx(
            'rounded-full px-3 py-1 text-[11px] font-medium transition-colors',
            active === n.id
              ? 'bg-white text-[#0F172A] shadow-[0_1px_2px_rgba(15,23,42,0.10)]'
              : 'text-[#64748B] hover:text-[#0F172A]',
          )}
        >
          {n.label}
        </button>
      ))}
    </nav>
  )
}

export function TopBar({
  clock, live, rate, gateways, user, fleets, activeFleet, onFleet,
  nav, activeNav, onNav, onSettings, settingsActive,
  fullMap, onFullMap,
}: TopBarProps) {
  // Only the sites this account may open (see allowedFleetIds). The session is loaded
  // once per page and cached, so this costs no extra request.
  const sessionState = useSession()
  const shownFleets = fleets && sessionState.status === 'signed-in'
    ? visibleFleetPills(fleets, sessionState.session)
    : fleets
  // Reports is admin-only at every site (auth/useCan.ts ADMIN_ONLY_RESOURCES). Hidden
  // while the session loads too, so it never flashes for a non-admin. A section nav left
  // with one item (just Command/Live) is not a choice, so it is not drawn at all.
  const isAdmin = sessionState.status === 'signed-in' && sessionState.session.is_admin
  const shownNavItems = nav?.filter((n) => n.id !== 'reports' || isAdmin)
  const shownNav = shownNavItems && shownNavItems.length > 1 ? shownNavItems : undefined
  const telemetry = [
    gateways != null ? `${gateways} GATEWAYS` : null,
    rate ?? null,
  ].filter(Boolean)

  return (
    <header className="flex items-center gap-3 rounded-[14px] border border-[rgba(15,23,42,0.07)] bg-white px-4 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <img src={transvoltLogo} alt="Transvolt" className="h-[18px] w-auto shrink-0" />

      <span
        className={clsx(
          'ml-1 flex h-6 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold',
          live ? 'bg-[#F0FDF4] text-[#16A34A]' : 'bg-[#F1F5F9] text-[#64748B]',
        )}
      >
        <span
          className={clsx(
            'h-1.5 w-1.5 rounded-full',
            live ? 'bg-[#16A34A] motion-safe:animate-pulse' : 'bg-[#94A3B8]',
          )}
        />
        {live ? 'LIVE' : 'OFFLINE'}
      </span>

      {shownFleets && shownFleets.length > 0 ? (
        <div className="ml-1">
          <PillNav items={shownFleets} active={activeFleet} onPick={onFleet} label="Fleet" />
        </div>
      ) : null}
      {shownNav ? (
        <PillNav items={shownNav} active={activeNav} onPick={onNav} label="Section" />
      ) : null}

      <div className="ml-auto flex items-center gap-3">
        {onFullMap ? (
          <button
            type="button"
            onClick={onFullMap}
            title={fullMap ? 'Back to command centre (Esc)' : 'Full map — live vehicles only'}
            aria-label={fullMap ? 'Exit full map' : 'Full map'}
            aria-pressed={fullMap}
            className={clsx(
              'flex h-8 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]',
              fullMap
                ? 'bg-[#0F172A] text-white shadow-[0_1px_2px_rgba(15,23,42,0.10)]'
                : 'text-[#64748B] hover:bg-[#EEF2F7] hover:text-[#0F172A]',
            )}
          >
            {fullMap
              ? <Minimize2 size={14} strokeWidth={2.2} />
              : <Maximize2 size={14} strokeWidth={2.2} />}
            Full map
          </button>
        ) : null}
        {onSettings ? (
          <button
            type="button"
            onClick={onSettings}
            title="Settings"
            aria-label="Settings"
            aria-current={settingsActive ? 'page' : undefined}
            className={clsx(
              'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
              settingsActive
                ? 'bg-[#0F172A] text-white shadow-[0_1px_2px_rgba(15,23,42,0.10)]'
                : 'text-[#64748B] hover:bg-[#EEF2F7] hover:text-[#0F172A]',
            )}
          >
            <Settings size={16} strokeWidth={2.2} />
          </button>
        ) : null}
        <span className="font-mono text-[13px] tabular-nums text-[#0F172A]">
          {clock}
          <span className="ml-1 text-[11px] text-[#94A3B8]">IST</span>
        </span>
        {telemetry.length > 0 ? (
          <span className="hidden items-center gap-1.5 font-mono text-[11px] text-[#94A3B8] lg:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
            {telemetry.join(' · ')}
          </span>
        ) : null}
        {user ? (
          <>
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0F172A] text-[11px] font-semibold text-white"
              title={user}
            >
              {initials(user)}
            </span>
            {/* GET /auth/logout clears fleet_session only -- not the Microsoft tenant
                session -- and 302s to /app/. return_to is this origin so Vite :5173
                does not land on serving :8001. */}
            <a
              href={logoutHref(window.location.origin)}
              title="Sign out"
              aria-label="Sign out"
              className="flex h-8 w-8 items-center justify-center rounded-full text-[#64748B]
                        transition-colors hover:bg-[#EEF2F7] hover:text-[#0F172A]"
            >
              <LogOut size={15} strokeWidth={2.2} />
            </a>
          </>
        ) : null}
      </div>
    </header>
  )
}
